/**
 * `SettlementRailSection` — per-account settlement-rail preference
 * picker (ADR-0081). Mounted by `SettingsIntegrations.tsx` below the
 * provider cards: the connected rails the merchant sees in those cards
 * are exactly the options selectable here.
 *
 * The preference is the per-ACCOUNT enum the rail selector reads before
 * routing a `place_order`. A single-rail merchant gets it auto-set on
 * connect (server-side, ADR-0081 Part 1); this control is how a
 * MULTI-rail merchant chooses among connected rails, and how anyone
 * inspects / corrects the current routing.
 *
 * Two radio options — Stripe, Shopify. An option is DISABLED unless the
 * account has that rail connected (the GET returns the connected set).
 * Selecting an unconnected rail is also hard-rejected server-side
 * (`rail_not_connected`); the disable is the UI half of that gate so a
 * merchant never selects a rail with no connector (the silent
 * place_order-abort trap).
 *
 * Role gate: owner/admin only (the server returns 403 for
 * reviewer/viewer). The section renders nothing for unprivileged roles,
 * mirroring `BuyerDetailRetentionSection`.
 *
 * State:
 *   - Bootstrap: GET on mount → current rail + connected set.
 *   - Edit: PUT on radio change; optimistic update; rollback + toast on
 *     failure (including `rail_not_connected`, defensively).
 */

import { useCallback, useEffect, useState } from 'react';
import { CreditCard, ShoppingBag } from 'lucide-react';

import type { UserRole } from '@sill/types';

import { useOptionalHttp } from '../../api/http-context.js';
import { useOptionalAuth } from '../../app/auth-context.js';
import { useToast } from '../../app/providers.js';
import { useC } from '../../lib/theme.js';
import { Card } from '../../components/atoms/index.js';
import {
  getSettlementRail,
  putSettlementRail,
  type ConnectedRails,
  type SettlementRail,
} from '../../api/account-settlement-rail.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

export interface SettlementRailSectionProps {
  /** Optional role override for tests that don't wire `<AuthProvider />`. */
  role_override?: UserRole;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; rail: SettlementRail; connected: ConnectedRails }
  | { kind: 'forbidden' }
  | { kind: 'error'; message: string };

interface RailOption {
  readonly value: SettlementRail;
  readonly label: string;
  readonly hint: string;
  readonly icon: typeof CreditCard;
}

const OPTIONS: readonly RailOption[] = [
  {
    value: 'stripe',
    label: 'Stripe',
    hint: 'Route place_order payments through your connected Stripe account.',
    icon: CreditCard,
  },
  {
    value: 'shopify',
    label: 'Shopify',
    hint: 'Route place_order payments through your connected Shopify store. Test mode only — live Shopify settlement is not enabled yet.',
    icon: ShoppingBag,
  },
];

export function SettlementRailSection({
  role_override,
}: SettlementRailSectionProps = {}): JSX.Element | null {
  const C = useC();
  const http = useOptionalHttp();
  const auth = useOptionalAuth();
  const toast = useToast();

  const role: UserRole | null =
    role_override !== undefined
      ? role_override
      : auth !== null && auth.state.kind === 'authenticated'
        ? auth.state.user.role
        : null;

  const can_read = role === 'owner' || role === 'admin';
  const can_write = can_read;

  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [pending, setPending] = useState<SettlementRail | null>(null);

  useEffect(() => {
    if (!can_read) {
      setState({ kind: 'forbidden' });
      return;
    }
    if (http === null) return;
    let cancelled = false;
    setState({ kind: 'loading' });
    void (async () => {
      const result = await getSettlementRail(http);
      if (cancelled) return;
      switch (result.kind) {
        case 'ok':
          setState({
            kind: 'ready',
            rail: result.rail,
            connected: result.connected,
          });
          break;
        case 'forbidden':
          setState({ kind: 'forbidden' });
          break;
        case 'error':
          setState({ kind: 'error', message: result.message });
          break;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [http, can_read]);

  const onChoose = useCallback(
    async (next: SettlementRail): Promise<void> => {
      if (!can_write) return;
      if (state.kind !== 'ready') return;
      if (state.rail === next) return;
      // UI half of the connected-rail gate — never PUT an unconnected
      // rail (the server also 409s `rail_not_connected`).
      if (!state.connected[next]) return;
      if (http === null) return;
      const previous = state.rail;
      const connected = state.connected;
      setPending(next);
      // Optimistic flip.
      setState({ kind: 'ready', rail: next, connected });
      const result = await putSettlementRail(http, next);
      setPending(null);
      switch (result.kind) {
        case 'ok':
          setState({ kind: 'ready', rail: result.rail, connected });
          toast({
            tone: 'lime',
            title: 'Settlement rail saved.',
            body: 'New place_order payments route through this rail.',
          });
          break;
        case 'rail_not_connected':
          setState({ kind: 'ready', rail: previous, connected });
          toast({
            tone: 'coral',
            title: 'That rail is not connected.',
            body: 'Connect the rail above before routing payments to it.',
          });
          break;
        case 'forbidden':
          setState({ kind: 'ready', rail: previous, connected });
          toast({
            tone: 'coral',
            title: 'Your role cannot change this setting.',
          });
          break;
        case 'bad_request':
          setState({ kind: 'ready', rail: previous, connected });
          toast({
            tone: 'coral',
            title: 'That value is not a supported rail.',
          });
          break;
        case 'error':
          setState({ kind: 'ready', rail: previous, connected });
          toast({
            tone: 'coral',
            title: 'Could not save settlement rail.',
            body: result.message,
          });
          break;
      }
    },
    [can_write, http, state, toast],
  );

  if (!can_read) return null;

  return (
    <Card className="p-5 sm:p-6">
      <div className="mb-5 pb-4" style={{ borderBottom: `1px solid ${C.border}` }}>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 2,
            marginBottom: 6,
          }}
        >
          SETTLEMENT RAIL
        </div>
        <div
          style={{
            fontFamily: serifFont,
            fontSize: 22,
            color: C.text,
            letterSpacing: -0.5,
            marginBottom: 4,
          }}
        >
          Which rail authorizes place_order payments
        </div>
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
          Sill routes each verified place_order to exactly one settlement
          rail. With a single connector this is set automatically. Pick a
          rail here only when more than one is connected — you can only
          choose a rail that has a live connection above.
        </div>
      </div>

      {state.kind === 'loading' ? (
        <div
          style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, letterSpacing: 1 }}
        >
          Reading current rail…
        </div>
      ) : null}

      {state.kind === 'error' ? (
        <div
          className="mt-2"
          style={{ fontFamily: sansFont, fontSize: 12, color: C.coralFg, lineHeight: 1.5 }}
        >
          Couldn't load settlement rail — {state.message}
        </div>
      ) : null}

      {state.kind === 'ready' ? (
        <fieldset
          aria-label="Settlement rail"
          className="space-y-2"
          style={{ border: 'none', padding: 0, margin: 0 }}
        >
          {OPTIONS.map((opt) => {
            const checked = state.rail === opt.value;
            const is_connected = state.connected[opt.value];
            const is_pending = pending === opt.value;
            // Disabled when: role can't write, a PUT is in flight, OR
            // the rail has no connector.
            const disabled = !can_write || pending !== null || !is_connected;
            const Icon = opt.icon;
            return (
              <label
                key={opt.value}
                className="flex items-start gap-3 p-3"
                style={{
                  background: checked ? C.surface2 : 'transparent',
                  border: `1px solid ${checked ? C.lime : C.border}`,
                  borderRadius: 2,
                  opacity: (pending !== null && !is_pending) || !is_connected ? 0.6 : 1,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                }}
              >
                <input
                  type="radio"
                  name="settlement_rail"
                  value={opt.value}
                  checked={checked}
                  disabled={disabled}
                  onChange={() => void onChoose(opt.value)}
                  className="mt-0.5"
                  style={{ accentColor: C.lime }}
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Icon size={14} color={C.text} />
                    <div
                      style={{
                        fontFamily: sansFont,
                        fontSize: 13,
                        color: C.text,
                        fontWeight: 500,
                      }}
                    >
                      {opt.label}
                    </div>
                    {!is_connected ? (
                      <span
                        style={{
                          fontFamily: monoFont,
                          fontSize: 9,
                          color: C.muted,
                          letterSpacing: 1,
                          border: `1px solid ${C.border}`,
                          borderRadius: 2,
                          padding: '1px 5px',
                        }}
                      >
                        NOT CONNECTED
                      </span>
                    ) : null}
                  </div>
                  <div
                    style={{
                      fontFamily: sansFont,
                      fontSize: 12,
                      color: C.muted,
                      marginTop: 2,
                      lineHeight: 1.5,
                    }}
                  >
                    {opt.hint}
                  </div>
                </div>
              </label>
            );
          })}
        </fieldset>
      ) : null}
    </Card>
  );
}
